import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../../context/LocalizationContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useAppContext } from "../../context/AppContext";
import HomeIcon from "../../assets/icons/home.svg";
import HumanIcon from "../../assets/icons/human.svg";
import "./VitalsCheck.css";
import HumanScanVideo from "../../assets/videos/human_scan.mp4";
import Header from "../shared/Header";
import arrow from "../../assets/icons/arrow.svg";

const VitalsCheck = ({ userData }) => {
  const navigate = useNavigate();
  const { t, isRTL } = useLocalization();
  const { subscribe } = useWebSocket();
  const { measures, setMeasures } = useAppContext();
  const [isMeasuring, setIsMeasuring] = useState(false);

  // Mapping function to localize vital names
  const getLocalizedVitalName = (englishName) => {
    const vitalMap = {
      "Muscle Mass": t("vitalsCheck.vitals.muscleMass"),
      Weight: t("vitalsCheck.vitals.weight"),
      "Bone Mass": t("vitalsCheck.vitals.boneMass"),
      "Heart pulse": t("vitalsCheck.vitals.heartPulse"),
      "Fat percentage": t("vitalsCheck.vitals.fatPercentage"),
      "Blood pressure": t("vitalsCheck.vitals.bloodPressure"),
      BMI: t("vitalsCheck.vitals.bmi"),
      "Metabolic Age": t("vitalsCheck.vitals.metabolicAge"),
      "Nerve Health": t("vitalsCheck.vitals.nerveHealth"),
      "Fat Ratio": t("vitalsCheck.vitals.fatRatio"),
      Hydration: t("vitalsCheck.vitals.hydration"),
      Height: t("vitalsCheck.vitals.height"),
      "Heart Rate": t("vitalsCheck.vitals.heartRate"),
      SPO2: t("vitalsCheck.vitals.spo2"),
      Temperature: t("vitalsCheck.vitals.temperature"),
    };
    return vitalMap[englishName] || englishName;
  };

  // Map measurement type to display format
  const getMeasurementDisplay = (measurementType) => {
    const measurementMap = {
      WEIGHT: {
        name: "Weight",
      },
      HEIGHT: {
        name: "Height",
      },
      BMI: {
        name: "BMI",
      },
      HEART_RATE: {
        name: "Heart Rate",
      },
      SPO2: {
        name: "SPO2",
      },
      TEMPERATURE: {
        name: "Temperature",
      },
    };
    return measurementMap[measurementType] || null;
  };

  // Subscribe to Vitals event from WebSocket
  useEffect(() => {
    const handleVitalsEvent = (data) => {
      console.log("Vitals event received:", data);

      // Extract measurements from the payload
      const measurements =
        data?.payload?.measurements || data?.measurements || [];

      // Filter and map only the required measurements
      const requiredMeasurements = [
        "WEIGHT",
        "HEIGHT",
        "BMI",
        "HEART_RATE",
        "SPO2",
        "TEMPERATURE",
      ];

      const mappedMeasures = measurements
        .filter((m) => requiredMeasurements.includes(m.measurementType))
        .map((m) => {
          const display = getMeasurementDisplay(m.measurementType);
          if (!display) return null;

          return {
            name: display.name,
            value: m.value.toString(),
            unit: m.unit,
          };
        })
        .filter(Boolean); // Remove null values

      // Update measures in store and stop measuring
      setMeasures(mappedMeasures);
      setIsMeasuring(false);
    };

    // Subscribe to the Vitals event
    const unsubscribe = subscribe("Vitals", handleVitalsEvent);

    return () => {
      unsubscribe();
    };
  }, [subscribe, setMeasures]);

  // Check measures in store on mount
  useEffect(() => {
    console.log("measures from store", measures);
    if (measures && Array.isArray(measures) && measures.length > 0) {
      // Measures exist in store, don't start measuring
      setIsMeasuring(false);
    } else {
      // No measures in store, start loading
      setIsMeasuring(true);
    }
  }, [measures]);

  return (
    <div className="main-container">
      <Header text={t("vitalsCheck.title")} userData={userData} />
      {isMeasuring ? (
        <>
          {/* Video Background for measuring phase */}
          <video className="video-background" autoPlay loop muted playsInline>
            <source src={HumanScanVideo} type="video/mp4" />
          </video>
          <div className="description-container" style={{ margin: "5% 7%" }}>
            {t("vitalsCheck.instructions")}
          </div>
        </>
      ) : (
        <>
          {/* Results phase with human.svg background */}
          <div className="results-container">
            <div className="human-background">
              <img
                src={HumanIcon}
                alt="Human body scan"
                className="human-svg"
              />
            </div>

            <div className="vitals-grid">
              {measures &&
                Array.isArray(measures) &&
                measures.map((vital, index) => (
                  <div key={index} className="vital-card">
                    <div className="corner-top-right"></div>
                    <div className="corner-bottom-left"></div>
                    <div className="vital-card-header">
                      <div className="vital-name">
                        {getLocalizedVitalName(vital.name)}
                      </div>
                    </div>
                    <div className="vital-value-container">
                      <div className="vital-value">{vital.value}</div>
                      <div className="vital-unit">{vital.unit}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      <div className="assessment-footer">
        <button
          className="footer-btn home-btn"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
          onClick={() => navigate("/dashboard")}
        >
          <img src={HomeIcon} alt="Home" />
        </button>
        {!isMeasuring && (
          <div className="footer-btn-container">
            <button
              className="next-btn talk-to-doctor-btn"
              style={{
                whiteSpace: "nowrap",
                padding: "6% 15%",
                color: "rgb(192 168 245)",
              }}
              onClick={() => navigate("/assessment")}
            >
              <span>{t("vitalsCheck.proceedToAssessments")}</span>
              <img
                src={arrow}
                alt="Next"
                style={{
                  marginTop: 2,
                  marginLeft: 3,
                  filter:
                    "brightness(0) saturate(100%) invert(75%) sepia(20%) saturate(200%) hue-rotate(250deg) brightness(120%) contrast(100%)",
                  transform: isRTL ? "rotate(180deg)" : "none",
                  transition: "transform 0.3s ease",
                }}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VitalsCheck;
